/**
 * Custom hook for managing saved chat sessions.
 * Handles fetching, loading state, and actions for sessions.
 */

import { useState, useCallback } from "react";
import type { SessionSummary } from "../types/api";
import type {
  ScientificSourceContent,
  MateriomFormulationContent,
} from "../types/api";
import type { ChatMessage } from "../types/frontend";
import {
  fetchSessions,
  fetchSessionMessages,
  deleteSession,
} from "../api/sessions";
import { useChatContext } from "../contexts/ChatContext";
import { useSourceContext } from "../contexts/SourcesContext";
import { convertApiSourceToFrontendSource } from "../utils/streamProcessor";

/**
 * Options for the useSavedChats hook.
 * @interface UseSavedChatsOptions
 * @property {function} onSessionLoaded - Optional callback when a session is loaded.
 */
interface UseSavedChatsOptions {
  onSessionLoaded?: () => void;
}

/**
 * Return type for the useSavedChats hook.
 * @interface UseSavedChatsReturn
 * @property {SessionSummary[]} sessions - Array of saved chat sessions.
 * @property {boolean} isLoading - Whether sessions are currently being fetched.
 * @property {boolean} isLoadingChat - Whether a chat is currently being loaded.
 * @property {string | null} error - Error message if fetch failed.
 * @property {function} loadSessions - Function to fetch sessions from the API.
 * @property {function} handleDelete - Placeholder function for deleting a session.
 * @property {function} handleOpenChat - Function for opening a chat session.
 */
interface UseSavedChatsReturn {
  sessions: SessionSummary[];
  isLoading: boolean;
  isLoadingChat: boolean;
  error: string | null;
  loadSessions: () => Promise<void>;
  handleDelete: (sessionId: string) => void;
  handleOpenChat: (sessionId: string) => Promise<void>;
}

/**
 * Custom hook for managing saved chat sessions.
 * @param {UseSavedChatsOptions} options - Optional configuration for the hook.
 * @returns {UseSavedChatsReturn} Object containing sessions state and management functions.
 */
export const useSavedChats = (
  options: UseSavedChatsOptions = {}
): UseSavedChatsReturn => {
  const { onSessionLoaded } = options;
  const { loadSession } = useChatContext();
  const { addSources, prependSources, clearSources, clearAttachedSources } =
    useSourceContext();

  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingChat, setIsLoadingChat] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetches saved sessions from the API and updates state.
   * Sessions are sorted by updated_at in descending order (most recent first).
   */
  const loadSessions = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetchSessions();
      // Sort sessions by updated_at descending (most recent first)
      const sortedSessions = [...response.sessions].sort(
        (a, b) => b.updated_at - a.updated_at
      );
      setSessions(sortedSessions);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to load sessions";
      setError(errorMessage);
      console.error("Failed to load sessions:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Placeholder function for deleting a session.
   * Currently only logs to console.
   * @param {string} sessionId - The ID of the session to delete.
   */
  const handleDelete = useCallback(async (sessionId: string) => {
    try {
      await deleteSession(sessionId);
      setSessions((prev) => prev.filter((s) => s.id !== sessionId));
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to delete session";
      setError(errorMessage);
      console.error("Failed to delete session:", err);
    }
  }, []);

  /**
   * Opens a chat session by fetching its messages and loading them into context.
   * Converts API sources to frontend format and adds them to the SourcesContext.
   * @param {string} sessionId - The ID of the session to open.
   */
  const handleOpenChat = useCallback(
    async (sessionId: string) => {
      setIsLoadingChat(true);
      setError(null);

      try {
        const response = await fetchSessionMessages(sessionId);

        // Clear existing sources before loading new session
        clearSources();
        clearAttachedSources();

        // Convert messages
        const convertedMessages: ChatMessage[] = response.messages
          .filter((message): message is ChatMessage => !!message)
          .map((message) => {
            const apiSources = message.sources as
              | (ScientificSourceContent | MateriomFormulationContent)[]
              | undefined;
            const convertedSources = apiSources?.map(
              convertApiSourceToFrontendSource
            );
            return {
              ...message,
              sources: convertedSources,
            };
          });

        // Extract and flatten all sources from converted messages
        const allSources = convertedMessages.flatMap(
          (msg) => msg.sources ?? []
        );

        // Add all sources to the SourcesContext
        if (allSources.length > 0) {
          addSources(allSources);
          prependSources(allSources);
        }

        // Load the session with converted messages
        loadSession(response.session_id, convertedMessages);
        onSessionLoaded?.();
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to load chat";
        setError(errorMessage);
        console.error("Failed to load chat:", err);
      } finally {
        setIsLoadingChat(false);
      }
    },
    [
      loadSession,
      onSessionLoaded,
      clearSources,
      clearAttachedSources,
      addSources,
      prependSources,
    ]
  );

  return {
    sessions,
    isLoading,
    isLoadingChat,
    error,
    loadSessions,
    handleDelete,
    handleOpenChat,
  };
};
